import { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { DisplayPriceGettingSpecificPriceParams } from '../../../../../../baseComponents/Displays/DisplayPrice'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import { TableComponentBaseScreenInnerProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderBoolean, CustomRenderImageToolTip, DeleteRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import locations from '../../../../../../utils/globals/locations'
import withLoading from '../../../../../../utils/hoc/withLoading'
import useProductActiveDictionary from '../../../../../../utils/hooks/product/useProductActiveDictionary'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { SpecificPriceRulesPayload } from '../../../../../../utils/reducers/products/specificPriceRules'
import CopyCategoryAddonsButton from '../CopyCategoryAddonsButton'
import ProductAddonSortButton from '../ProductAddonSortButton'
import { ProductAddonWithLang, TableProductAddonsUrlsTy } from '../type'

type TableProductAddonProps = TableComponentBaseScreenInnerProps & TableProductAddonsUrlsTy & { }

const TableProductAddon: React.FC<TableProductAddonProps & ReduxSpecificPricesTable> = ({ tableInstance, setOpened, setTableInstance, extraWhereParams, userPermissions, specificPriceRules, urlGetDataProductAddons, urlGetDataCategoryAddons, urlCopyProductAddons, urlUpdateAddonsOrder, field }) => {
    const { t } = useTwinTranslation()
    const { productActiveDict } = useProductActiveDictionary()
    const hasJustCategoryComplements = extraWhereParams?.hasJustCategoryComplements

    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [specificPriceRules?.arrSorted])

    const columns: ColumnTableSchema<ProductAddonWithLang> = [
        {
            id: 'ProductAddon.ProductImages.url',
            dataKey: 'ProductAddon.ProductImages.url',
            label: t('image', 'Imagen'),
            width: 75,
            customRender: (parameterValue, allRowData) => <CustomRenderImageToolTip className='table_products_image' value={parameterValue} allRowData={allRowData} />
        },
        {
            id: 'ProductAddonId',
            dataKey: 'ProductAddonId',
            label: t('id', 'ID'),
            width: 50,
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('reference', 'Referencia') }
            },
            minWidth: 120
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'price',
            dataKey: 'SpecificPriceRuleId',
            minWidth: 150,
            label: (props) => {
                const myValue = parseInt(props.selectedFilterValue || props.value || defaultSpecificPriceRuleId)
                return <span>{t('price', 'Precio') + ' (' + (specificPriceRules?.dictionary?.[myValue]?.name || t('notSelectedPriceRule', 'Sin tarifa selec.')) + ')'} </span>
            },
            simpleLabel: t('price', 'Precio'),
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: specificPriceRules?.dictionary || {}, label: t('specificPriceRule', 'Tarifa') }
            },
            generalSearchable: false,
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPriceGettingSpecificPriceParams value={allRowData?.calculated_price} CurrencyId={specificPriceRules?.dictionary[allRowData.SpecificPriceRuleId]?.CurrencyId || ''} with_vat={false} specificPriceRules={specificPriceRules} SpecificPriceRuleId={defaultSpecificPriceRuleId} />
            }
        },
        {
            id: 'active',
            dataKey: 'ProductAddon.active' as any,
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: productActiveDict, label: t('active', 'Activo') }
            },
            generalSearchable: false
        },
        {
            id: 'delete',
            dataKey: 'ProductAddonId',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: !hasJustCategoryComplements && permissionCheck(userPermissions, 'delete')
        },
    ]
    const children = useMemo(() => {
        if (hasJustCategoryComplements) {
            return <CopyCategoryAddonsButton url={urlCopyProductAddons} ProductId={extraWhereParams?.[field]} CategoryId={extraWhereParams?.CategoryMainId} getData={extraWhereParams?.getData} />
        } else {
            return <ProductAddonSortButton url={urlUpdateAddonsOrder} urlGetData={urlGetDataProductAddons}  extraWhereParams={{ [field]: extraWhereParams?.[field], SpecificPriceRuleId: defaultSpecificPriceRuleId }} tableInstance={tableInstance} userPermissions={userPermissions} />
        }
    }, [hasJustCategoryComplements, tableInstance, userPermissions, extraWhereParams, defaultSpecificPriceRuleId, urlCopyProductAddons, urlUpdateAddonsOrder, urlGetDataProductAddons, field])

    let tableParams = {
        getDataFrom: urlGetDataProductAddons,
        extraWhereParams: {
            [field]: extraWhereParams?.[field]
        } as TwinDictionary,
        className: '',
        extraWhereSubstituibleParams: {
            'SpecificPriceRuleId': defaultSpecificPriceRuleId
        } as TwinDictionary
    }
    if (hasJustCategoryComplements) {
        tableParams = {
            className: 'hasCategoryAddons',
            extraWhereParams: {
                'CategoryId': extraWhereParams?.CategoryMainId
            },
            extraWhereSubstituibleParams: {},
            getDataFrom: urlGetDataCategoryAddons
        }
    }
    return (
        <TwinTableLink columns={columns} name='subProductAddon' createRowDataLink={[locations.addon, ['$ProductAddonId', 'main']]}
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })} setTableInstance={setTableInstance} defaultOrder={{ 'defaultOrderField': 'order', order: 'asc' }} {...tableParams}>
            {children}
        </TwinTableLink>
    )
}


interface TableProductAddonsSellProps extends TableComponentBaseScreenInnerProps {}

export const TableProductAddonsSell: React.FC<TableProductAddonsSellProps> = ({...rest}) => {
    const data: TableProductAddonsUrlsTy = {
        field: 'ProductAddonRel.ProductId',
        urlGetDataProductAddons: '/api/app/product/productAddon/getAllProductAddonsComplete',
        urlGetDataCategoryAddons: '/api/app/product/category/categoryAddon/getAllCategoryAddons',
        urlCopyProductAddons: '/api/app/product/productAddon/copyCategoryAddonsToProduct',
        urlUpdateAddonsOrder: '/api/app/product/productAddon/updateProductAddonOrder'
    }
    return (<TableProductAddonConnect {...data } {...rest} />)
}

interface TableProductAddonsBuyProps extends TableComponentBaseScreenInnerProps {}

export const TableProductAddonsBuy: React.FC<TableProductAddonsBuyProps> = ({...rest}) => {
    const data: TableProductAddonsUrlsTy = {
        field: 'ProductAddonBuyRel.ProductId',
        urlGetDataProductAddons: '/api/app/product/productAddonBuy/getAllProductAddonsBuyComplete',
        urlGetDataCategoryAddons: '/api/app/product/category/categoryAddonBuy/getAllCategoryAddonsBuy',
        urlCopyProductAddons: '/api/app/product/productAddonBuy/copyCategoryAddonsBuyToProduct',
        urlUpdateAddonsOrder: '/api/app/product/productAddonBuy/updateProductAddonBuyOrder'
    }
    return (<TableProductAddonConnect {...data } {...rest} />)
}



const ProductAddonTableDispatch = {
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
}

type ReduxSpecificPricesTable = ConnectedProps<typeof ProductAddonTableConnect>
const mapProductAddonTableConnector = (state: AllReduxPayloads) => ({ specificPriceRules: state.specificPriceRules })
const ProductAddonTableConnect = connect(mapProductAddonTableConnector, ProductAddonTableDispatch)
const ProductAddonTableConnectLoading = withLoading(TableProductAddon, [{ fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }])

const TableProductAddonConnect = ProductAddonTableConnect(ProductAddonTableConnectLoading)